import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { NextPage } from '@cvx/nextpage';
import { AdminService } from '../../../../services/admin.service';
import { CalService } from '../../../../services/base/cal.service';

interface APIUser {
  facilityId: string;
  displayName: string;
  cai: string;
  ad: boolean;
  en: boolean;
  op: boolean;
  le: boolean;
  in: boolean;
  oa: boolean;
  ai: boolean;
}

interface User {

  userId: string;
  name: string;
  email?: string;
  phone?: string;
  administrator: boolean;
  inspections: boolean;
  operator: boolean;
  oa: boolean;
  airCompliance: boolean;
  leadEngineer: boolean;
  engineer: boolean;
}

@Component({
    selector: 'app-user-role-management',
    imports: [
        NextPage,
        CommonModule,
        FormsModule,
        TableModule,
        CheckboxModule,
        ButtonModule,
        InputTextModule,
    ],
    templateUrl: './user-role-management.component.html',
    styleUrl: './user-role-management.component.scss'
})
export class UserRoleManagementComponent implements OnInit {
  // User search properties
  searchUserId: string = '';
  prevSearchUserId: string = '';
  filteredUsers: User[] = [];
  allUsers: User[] = [];
  isLoading   = false;
  noResultsFound: boolean = false;
  showUserDetails: boolean = false;
  selectedUser: User | null = null;
  isDataLoading: boolean = true; // New property to track API data loading    // Add new user properties
  newUserId: string = '';
  userVerified: boolean = false;
  userExists: boolean = false; // New property to track if user already exists
  showEmptyFieldMessage: boolean = false; // New property to show empty field message
  userNotValid: boolean = false; // New property to track if user is not valid
  newUserRoles: User = {
    userId: '',
    name: '',
    administrator: false,
    inspections: false,
    operator : false,
    oa: false,
    airCompliance: false,
    leadEngineer: false,
    engineer: false
  };

  constructor(
    private adminService: AdminService,
    private messageService: MessageService,
    private calService: CalService  
  ) { }
    ngOnInit() {
    this.loadUserRoleData();
  }

  // Separate method to load user role data - can be reused for refresh
  loadUserRoleData() {
    // Set loading state to true before API call
    this.isDataLoading = true;
    
    this.adminService.getAdminUserRoleNotification().subscribe(
      response => {
        
        // Map API users to our User interface
        const apiUsers = Array.isArray(response) ? response as APIUser[] : [];
        this.allUsers = this.mapAPIUsersToUsers(apiUsers);
        this.filteredUsers = [...this.allUsers];
        
        // Set loading state to false once data is loaded
        this.isDataLoading = false;
      },
      error => {
        // Set loading state to false on error
        this.isDataLoading = false;
      }
    );
  }

  // Map API user data to our User interface
  mapAPIUsersToUsers(apiUsers: APIUser[]): User[] {
    return apiUsers.map(apiUser => {
      return {
        userId: apiUser.cai, // cai is userId
        name: apiUser.displayName, // displayName is name
        administrator: apiUser.ad, // ad is Administrator
        engineer: apiUser.en, // en is Engineer
        operator: apiUser.op, // op is operations
        leadEngineer: apiUser.le, // le is Lead Engineer
        inspections: apiUser.in, // in is Inspections
        oa: apiUser.oa, // oa is OA
        airCompliance: apiUser.ai // ai is Air Compliance
      };
    });
  }

  onSearchInputChange() {
    // If search field is empty, hide user details
    if (!this.searchUserId || this.searchUserId.trim() === '') {
      this.filteredUsers = [...this.allUsers];
      this.noResultsFound = false;
      this.showUserDetails = false;
    }
  }

  searchUser() {
    this.prevSearchUserId = this.searchUserId; 
    if (!this.searchUserId.trim()) {
      this.filteredUsers = [...this.allUsers];
      this.noResultsFound = false;
      this.showUserDetails = false;
      return;
    }
    
    const searchTerm = this.searchUserId.toLowerCase();
    
    // Find users matching the search term (exact or partial)
    this.filteredUsers = this.allUsers.filter(user => 
      user.userId.toLowerCase().includes(searchTerm) || 
      user.name.toLowerCase().includes(searchTerm)
    );
    
    this.noResultsFound = this.filteredUsers.length === 0;
    // Always keep table view, don't show user details
    this.showUserDetails = false;
    
  }
  
  saveChanges() {
    if (this.selectedUser) {
      this.updateUser(this.selectedUser);
    }
  }

  updateUser(user: User) {
    // Create the request body according to API requirements
    const updateData = {
      cai: user.userId,
      facilityId: this.adminService.getCurrentFacilityId(),
      ad: user.administrator,
      en: user.engineer,
      le: user.leadEngineer,
      op: user.operator,
      in: user.inspections,
      oa: user.oa,
      ai: user.airCompliance,
      enteredByCAI: this.adminService.cai
    };
    
    
    // Call the service method to update the user roles
    this.adminService.updateUserRole(updateData).subscribe(
      (response: any) => {
        if (response && response.success === false) {
          // Show error message with MessageService
          this.messageService.add({
            severity: 'error',
            summary: 'Update Failed',
            detail: `Failed to update user roles: ${response.error}`,
            life: 5000
          });
        } else {
          // Show success message with MessageService
          this.messageService.add({
            severity: 'success',
            summary: 'Update Successful',
            detail: `User ${user.userId} roles updated successfully!`,
            life: 3000
          });

          const currentUserCai = (localStorage.getItem('current_user_cai') || '').toLowerCase();
          if (user.userId.toLowerCase() === currentUserCai) {
            sessionStorage.removeItem('userRole');
            window.location.reload();
          }
        }
      },
      (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'An error occurred while updating user roles. Please try again.',
          life: 5000
        });
      }
    );
  }

  deleteUser(userId: any) {
    // Call the API to delete the user role
    this.adminService.deleteUserRole(userId.userId).subscribe(
      (response: any) => {
        if (response && response.success === false) {
          // Show error message with MessageService
          this.messageService.add({
            severity: 'error',
            summary: 'Delete Failed',
            detail: `Failed to delete user: ${response.error}`,
            life: 5000
          });        } else {
          // Show success message with MessageService
          this.messageService.add({
            severity: 'success',
            summary: 'User Deleted',
            detail: `User ${userId.userId} deleted successfully!`,
            life: 3000
          });
          
          // Refresh the data by calling getAdminUserRoleNotification
          this.loadUserRoleData();
        }
      },
      (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'An error occurred while deleting the user. Please try again.',
          life: 5000
        });
      }
    );
  }
    // Add new user methods
  verifyUserId() {
    this.isDataLoading = true; 
    
    // Check if input field is empty
    if (!this.newUserId || !this.newUserId.trim()) {
      this.isDataLoading = false;
      this.showEmptyFieldMessage = true;
      // Reset other flags
      this.userVerified = false;
      this.userExists = false;
      return;
    }
      // Reset all flags when input is not empty
    this.showEmptyFieldMessage = false;
    this.userVerified = false;
    this.userExists = false;
    this.userNotValid = false;
    
    // Check if user already exists in our list
    const existingUser = this.allUsers.find(user => 
      user.userId.toLowerCase() === this.newUserId.toLowerCase()
    );
    
    if (existingUser) {
      // User exists - set flag to show message below input field
      this.isDataLoading = false;
      this.userExists = true;
      return;
    }
      // Get user info by the entered newUserId - not the currently logged in user
    this.adminService.getUserInfoByUserID(this.newUserId).subscribe(userInfo => {
      
      // Check if userInfo is null or undefined
      if (!userInfo) {
        this.userNotValid = true;
        this.userVerified = false;
        this.isDataLoading = false;
        return;
      }
      
      // Set userVerified = true and use the entered ID
      this.userVerified = true;
      this.newUserRoles.userId = this.newUserId;
      
      // If we have user info, use it
      this.newUserRoles.name = userInfo.displayName || `New User (${this.newUserId})`;
      this.newUserRoles.email = userInfo.emailAddress || '';
      this.newUserRoles.phone = userInfo.ctnNumber || '';
      
      // Reset all roles to false
      this.newUserRoles.administrator = false;
      this.newUserRoles.inspections = false;
      this.newUserRoles.oa = false;
      this.newUserRoles.operator = false;
      this.newUserRoles.airCompliance = false;
      this.newUserRoles.leadEngineer = false;
      this.newUserRoles.engineer = false;
      this.isDataLoading = false;

    });
  }
  clearUserId() {
    this.newUserId = '';
    this.userVerified = false;
    this.userExists = false;
    this.showEmptyFieldMessage = false;
    this.userNotValid = false;
  }
  
  addUser() {
    if (!this.userVerified) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Verification Required',
        detail: 'Please verify the User ID first',
        life: 3000
      });
      return;
    }
    
    // Create the request body according to API requirements
    const addData = {
      cai: this.newUserRoles.userId,
      facilityId: this.adminService.getCurrentFacilityId(),
      ad: this.newUserRoles.administrator,
      en: this.newUserRoles.engineer,
      op: this.newUserRoles.operator,
      le: this.newUserRoles.leadEngineer,
      in: this.newUserRoles.inspections,
      oa: this.newUserRoles.oa,
      ai: this.newUserRoles.airCompliance,
      enteredByCAI: this.adminService.cai,
      displayName: this.newUserRoles.name
    };
    
    
    // Call the service method to add the new user role
    this.adminService.addUserRole(addData).subscribe(
      (response: any) => {
        // Check if response is explicitly marked as failed
        if (response && response.success === false) {
          // Show error message with MessageService
          this.messageService.add({
            severity: 'error',
            summary: 'Add Failed',
            detail: `${response.error || 'Failed to add user role'}`,
            life: 5000
          });
          
          // Don't show the role checkboxes in case of error
          this.userVerified = false;
        } else {
          // Handle success case - API returned 200 status code
          // Add the new user to our lists for immediate display
          const newUser: User = {
            userId: this.newUserRoles.userId,
            name: this.newUserRoles.name,
            email: this.newUserRoles.email,
            phone: this.newUserRoles.phone,
            administrator: this.newUserRoles.administrator,
            inspections: this.newUserRoles.inspections,
            operator: this.newUserRoles.operator,
            oa: this.newUserRoles.oa,
            airCompliance: this.newUserRoles.airCompliance,
            leadEngineer: this.newUserRoles.leadEngineer,
            engineer: this.newUserRoles.engineer
          };
          
          this.allUsers.push(newUser);
          this.filteredUsers = [...this.allUsers];
          
          this.messageService.add({
            severity: 'success',
            summary: 'User Added',
            detail: `User ${this.newUserRoles.userId} added successfully!`,
            life: 3000
          });
          
          // Reset the form
          this.newUserId = '';
          this.userVerified = false;
        }
      },
      (error) => {
        let errorMessage = 'An error occurred while adding the user role.';
        
        // Check if error has a readable message
        if (error.error && typeof error.error === 'string') {
          errorMessage = error.error;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
          life: 5000
        });
        
        // Don't show the role checkboxes in case of error
        this.userVerified = false;
      }
    );
  }
  // Handle input change to hide messages when user starts typing
  onUserIdInputChange() {
    if (this.newUserId && this.newUserId.trim() !== '') {
      this.showEmptyFieldMessage = false;
      this.userNotValid = false;
    }
  }

  // Clear search user ID when switching to User User ID/Roles tab
  clearSearchUserId() {
    this.searchUserId = '';
    this.filteredUsers = [...this.allUsers]; // Reset filtered users to show all users
    this.showUserDetails = false; // Hide user details panel
    this.selectedUser = null; // Clear selected user
  }
}
